import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { adminService } from '../../services/adminService';

const POLL_INTERVAL_MS = 5000;

const statusStyles = {
    queued: { background: '#fef3c7', color: '#92400e' },
    running: { background: '#dbeafe', color: '#1e40af' },
    completed: { background: '#dcfce7', color: '#166534' },
    failed: { background: '#fee2e2', color: '#991b1b' },
    cancelled: { background: '#e5e7eb', color: '#374151' },
};

const isActive = (trigger) => trigger?.status?.status === 'queued' || trigger?.status?.status === 'running';

const ApiTrigger = ({ tabname }) => {
    const toast = useRef(null);
    const previousStatuses = useRef({});
    const [triggers, setTriggers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [runningKey, setRunningKey] = useState(null);
    const [expandedKey, setExpandedKey] = useState(null);
    const [cancellingKey, setCancellingKey] = useState(null);

    const loadTriggers = () => {
        return adminService.getapitriggers().then((response) => {
            if (response?.status === 200 && Array.isArray(response?.data?.data)) {
                const list = response.data.data;

                // tell the user when a run they were watching finishes
                list.forEach((trigger) => {
                    const before = previousStatuses.current[trigger.key];
                    const now = trigger.status?.status;
                    const wasActive = before === 'queued' || before === 'running';
                    if (wasActive && now === 'completed') {
                        toast.current.show({ severity: 'success', summary: 'Completed', detail: `${trigger.label} completed`, life: 6000 });
                    } else if (wasActive && now === 'failed') {
                        toast.current.show({ severity: 'error', summary: 'Failed', detail: `${trigger.label} failed`, life: 8000 });
                    }
                    previousStatuses.current[trigger.key] = now;
                });

                setTriggers(list);
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: response?.data?.message || 'Unable to load triggers', life: 3000 });
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        loadTriggers();
    }, []);

    // poll only while something is queued or running
    const anyActive = triggers.some(isActive);
    useEffect(() => {
        if (!anyActive) {
            return undefined;
        }
        const timer = setInterval(loadTriggers, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [anyActive]);

    const handleRun = (trigger) => {
        if (!window.confirm(`Run "${trigger.label}" now?`)) {
            return;
        }

        setRunningKey(trigger.key);
        adminService.runapitrigger({ command: trigger.key }).then((response) => {
            if (response?.status === 200) {
                toast.current.show({ severity: 'info', summary: 'Queued', detail: response?.data?.message, life: 4000 });
                return loadTriggers();
            }
            toast.current.show({ severity: 'error', summary: 'Error', detail: response?.data?.message || 'Something went wrong', life: 3000 });
        }).catch(() => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
        }).finally(() => {
            setRunningKey(null);
        });
    };

    const handleCancel = (trigger) => {
        if (!window.confirm(`Cancel "${trigger.label}"? Waiting jobs are removed; a step already executing cannot be interrupted.`)) {
            return;
        }

        setCancellingKey(trigger.key);
        adminService.cancelapitrigger({ command: trigger.key }).then((response) => {
            if (response?.status === 200) {
                toast.current.show({ severity: 'warn', summary: 'Cancelled', detail: response?.data?.message, life: 4000 });
                return loadTriggers();
            }
            toast.current.show({ severity: 'error', summary: 'Error', detail: response?.data?.message || 'Something went wrong', life: 3000 });
        }).catch(() => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
        }).finally(() => {
            setCancellingKey(null);
        });
    };

    return (
        <>
            <Toast ref={toast} />
            <div className='card-style-padding'>
                <h6 style={{ fontWeight: 600, color: '#1a1f3a', marginBottom: '18px' }}>{tabname}</h6>

                {isLoading ? (
                    <p>Loading...</p>
                ) : triggers.length === 0 ? (
                    <p>No triggers available.</p>
                ) : (
                    <table className='table' style={{ textAlign: 'left' }}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th style={{ width: '220px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {triggers.map((trigger) => {
                                const run = trigger.status;
                                return (
                                    <React.Fragment key={trigger.key}>
                                        <tr>
                                            <td>{trigger.label}</td>
                                            <td>{trigger.description}</td>
                                            <td>
                                                {run ? (
                                                    <>
                                                        <span style={{ ...statusStyles[run.status], padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
                                                            {run.status}
                                                        </span>
                                                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                                            {run.finished_at || run.started_at || run.queued_at}
                                                            {run.triggered_by ? ` by ${run.triggered_by}` : ''}
                                                        </div>
                                                        {run.detail ? <div style={{ fontSize: '12px', color: '#6b7280' }}>{run.detail}</div> : null}
                                                        {run.output_tail ? (
                                                            <a href='#!' style={{ fontSize: '12px' }} onClick={(e) => { e.preventDefault(); setExpandedKey(expandedKey === trigger.key ? null : trigger.key); }}>
                                                                {expandedKey === trigger.key ? 'Hide output' : 'Show output'}
                                                            </a>
                                                        ) : null}
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#6b7280' }}>Not run recently</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    label='Run'
                                                    loading={runningKey === trigger.key || isActive(trigger)}
                                                    disabled={runningKey !== null || isActive(trigger)}
                                                    onClick={() => handleRun(trigger)}
                                                />
                                                <Button
                                                    label='Cancel'
                                                    className='p-button-danger p-button-outlined'
                                                    style={{ marginLeft: '8px' }}
                                                    loading={cancellingKey === trigger.key}
                                                    disabled={!isActive(trigger) || cancellingKey !== null}
                                                    onClick={() => handleCancel(trigger)}
                                                />
                                            </td>
                                        </tr>
                                        {expandedKey === trigger.key && run?.output_tail ? (
                                            <tr>
                                                <td colSpan={4}>
                                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>{run.output_tail}</pre>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default ApiTrigger;
